/**
 * Auth Context — code-exchange flow via centralized auth-service
 *
 * Flow: Redirect to auth-service login → user authenticates → redirect back
 * with auth code → exchange for tokens → store in localStorage
 *
 * Follows the same pattern as Juggler's AuthProvider.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

import { authServiceUrl, authFrontendUrl } from '../proxy-config';
const AUTH_SERVICE_URL = authServiceUrl;
const AUTH_FRONTEND_URL = authFrontendUrl;
const APP_URL = window.location.origin;
const TOKEN_KEY = 'billing-access-token';
const REFRESH_KEY = 'billing-refresh-token';

function parseJwt(token) {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

function userFromToken(token) {
  const decoded = parseJwt(token);
  if (!decoded || decoded.exp * 1000 < Date.now()) return null;
  return {
    id: decoded.sub,
    email: decoded.email,
    name: decoded.name,
    roles: decoded.roles || [],
    plans: decoded.plans || {},
    isAdmin: (decoded.roles || []).includes('admin')
  };
}

// PKCE (RFC 7636) — generate code_verifier and code_challenge for OAuth public clients
function base64URLEncode(buffer) {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function generateCodeVerifier() {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64URLEncode(array);
}

async function generateCodeChallenge(verifier) {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return base64URLEncode(hash);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Handle auth callback — exchange code for tokens
  useEffect(() => {
    if (window.location.pathname !== '/auth/callback') return;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');

    if (!code) {
      if (params.get('error')) {
        // We navigated here to SHOW the exchange failure — let AuthCallbackGuard
        // render the "Authentication Failed" screen instead of silently bouncing
        // to login and dropping the error (999.1594 harrison BLOCK).
        setLoading(false);
        return;
      }
      // Landed on callback without a code — redirect to login
      console.warn('Auth callback reached without code, redirecting to login');
      setLoading(false);
      const callbackUrl = `${APP_URL}/auth/callback`;
      window.location.href = `${AUTH_FRONTEND_URL}/login?app=billing&redirect=${encodeURIComponent(callbackUrl)}`;
      return;
    }

    fetch(`${AUTH_SERVICE_URL}/api/auth/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        code,
        app: 'billing',
        code_verifier: localStorage.getItem('billing-pkce-verifier')
      })
    })
      .then(res => res.json())
      .then(data => {
        if (data.access_token) {
          localStorage.setItem(TOKEN_KEY, data.access_token);
          if (data.refresh_token) localStorage.setItem(REFRESH_KEY, data.refresh_token);

          const u = userFromToken(data.access_token);
          setUser(u);

          // Navigate to the original URL the user was trying to reach.
          // Use window.location.replace (not replaceState) so React Router
          // re-renders with the correct route. Tokens are in localStorage
          // and will be picked up by the session restore on mount.
          const returnTo = localStorage.getItem('billing-return-to') || '/plans';
          localStorage.removeItem('billing-return-to');
          window.location.replace(returnTo);
        } else {
          throw new Error(data.error || 'No access token in response');
        }
      })
      .catch(err => {
        console.error('Auth callback failed:', err);
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_KEY);
        // 999.1594 — surface the failure via the existing /auth/callback?error=...
        // dead-end guard (AuthCallbackGuard already renders an "Authentication
        // Failed" screen for this) instead of silently landing back on /plans with
        // no explanation.
        const message = err && err.message ? err.message : 'exchange_failed';
        window.location.replace(`/auth/callback?error=${encodeURIComponent(message)}`);
      })
      .finally(() => setLoading(false));
  }, []);

  // Try to restore session on mount
  useEffect(() => {
    if (window.location.pathname === '/auth/callback') return;

    let cancelled = false;

    async function restoreSession() {
      try {
        // Try existing token
        const token = localStorage.getItem(TOKEN_KEY);
        if (token) {
          const u = userFromToken(token);
          if (u) {
            if (!cancelled) setUser(u);
            return;
          }
        }

        // Try refresh
        const refreshToken = localStorage.getItem(REFRESH_KEY);
        if (refreshToken) {
          try {
            const res = await fetch(`${AUTH_SERVICE_URL}/api/auth/refresh`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ refreshToken })
            });

            if (res.ok) {
              const data = await res.json();
              if (!cancelled && data.tokens?.accessToken) {
                localStorage.setItem(TOKEN_KEY, data.tokens.accessToken);
                if (data.tokens.refreshToken) localStorage.setItem(REFRESH_KEY, data.tokens.refreshToken);
                const u = userFromToken(data.tokens.accessToken);
                setUser(u);
                return;
              }
            }
          } catch {
            // Refresh failed
          }
        }

        // No local tokens — try SSO cookie (user may be logged in via another app)
        try {
          const ssoRes = await fetch(`${AUTH_SERVICE_URL}/api/auth/sso-check`, {
            credentials: 'include'
          });
          if (ssoRes.ok) {
            const ssoData = await ssoRes.json();
            if (ssoData.authenticated) {
              // SSO valid — exchange for tokens
              const tokenRes = await fetch(`${AUTH_SERVICE_URL}/api/auth/sso-token`, {
                method: 'POST',
                credentials: 'include'
              });
              if (tokenRes.ok) {
                const tokenData = await tokenRes.json();
                if (!cancelled && tokenData.tokens?.accessToken) {
                  localStorage.setItem(TOKEN_KEY, tokenData.tokens.accessToken);
                  if (tokenData.tokens.refreshToken) localStorage.setItem(REFRESH_KEY, tokenData.tokens.refreshToken);
                  const u = userFromToken(tokenData.tokens.accessToken);
                  setUser(u);
                  return;
                }
              }
            }
          }
        } catch {
          // SSO check failed — not critical
        }

        // No valid session
        if (!cancelled) {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(REFRESH_KEY);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    restoreSession();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async () => {
    // Save current path so we return here after login
    localStorage.setItem('billing-return-to', window.location.pathname + window.location.search);
    const callbackUrl = `${APP_URL}/auth/callback`;
    // PKCE: generate verifier + challenge, store verifier for the exchange step
    const verifier = generateCodeVerifier();
    const challenge = await generateCodeChallenge(verifier);
    localStorage.setItem('billing-pkce-verifier', verifier);
    window.location.href = `${AUTH_FRONTEND_URL}/login?app=billing&redirect=${encodeURIComponent(callbackUrl)}&code_challenge=${challenge}&code_challenge_method=S256`;
  }, []);

  const [loggingOut, setLoggingOut] = useState(false);

  const logout = useCallback(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    // Set flag BEFORE clearing tokens to prevent ProtectedRoute from calling login()
    setLoggingOut(true);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem('billing-return-to');
    setUser(null);
    // Redirect through auth-service to clear its session + Google session,
    // then redirect back to our plans page
    let logoutUrl = `${AUTH_SERVICE_URL}/api/auth/logout-redirect?redirect=${encodeURIComponent(APP_URL + '/plans')}`;
    if (token) logoutUrl += `&token=${encodeURIComponent(token)}`;
    window.location.href = logoutUrl;
  }, []);

  /**
   * Refresh the JWT to pick up updated plan claims (e.g., after checkout)
   */
  const refreshSession = useCallback(async () => {
    const refreshToken = localStorage.getItem(REFRESH_KEY);
    if (!refreshToken) return;

    try {
      const res = await fetch(`${AUTH_SERVICE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken })
      });

      if (res.ok) {
        const data = await res.json();
        if (data.tokens?.accessToken) {
          localStorage.setItem(TOKEN_KEY, data.tokens.accessToken);
          if (data.tokens.refreshToken) localStorage.setItem(REFRESH_KEY, data.tokens.refreshToken);
          const u = userFromToken(data.tokens.accessToken);
          setUser(u);
        }
      }
    } catch {
      // Silent failure
    }
  }, []);

  // Session heartbeat — poll auth-service every 60s to detect sign-off from another app
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) return;

      try {
        const res = await fetch(`${AUTH_SERVICE_URL}/api/auth/session-alive`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (res.status === 401) {
          // Session ended from another app — clear local state and redirect through auth-service
          setLoggingOut(true);
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(REFRESH_KEY);
          setUser(null);
          window.location.href = `${AUTH_SERVICE_URL}/api/auth/logout-redirect?redirect=${encodeURIComponent(APP_URL + '/plans')}`;
        }
      } catch {
        // Network error — skip this check
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [user]);

  const value = {
    user,
    loading,
    loggingOut,
    isAuthenticated: !!user,
    login,
    logout,
    refreshSession
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
